// uploadDocumentSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as UploadDocumentAPI from "./UploadDocumentAPI";

// Thunks
export const loadDocumentDetails = createAsyncThunk(
  "uploadDoc/fetchDetails",
  async (pdeId, { rejectWithValue }) => {
    try {
      return await UploadDocumentAPI.fetchUploadedDocDetails(pdeId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const previewDeed = createAsyncThunk(
  "uploadDoc/preview",
  async (pdeId, { rejectWithValue }) => {
    try {
      return await UploadDocumentAPI.fetchDeedBase64(pdeId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitDocuments = createAsyncThunk(
  "uploadDoc/submit",
  async ({ formData, isPOA, isOther }, { rejectWithValue }) => {
    try {
      await UploadDocumentAPI.uploadDocuments(formData, isPOA, isOther);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  details: null,
  base64: null,
  status: "idle",
  error: null,
};

const uploadDocumentSlice = createSlice({
  name: "uploadDoc",
  initialState,
  reducers: {
    resetUploadDoc: () => initialState,
    setUploadDetails: (state, action) => {
      // the API returns uploadDocs:[{…}], so we take the first
      const doc = action.payload;
      state.details = {
        // store exactly what your formReset useEffect expects:
        stampDutyCert: doc.document_StampDuty?.split("\\").pop() || "",
        stampIssueDate: doc.entry_date || "",
        history: doc.history || "",
        additionalDetails: doc.textField || "",
        east: doc.east || "",
        west: doc.west || "",
        north: doc.north || "",
        south: doc.south || "",
        idCardName: doc.document_ID?.split("\\").pop() || "",
        propertyName: doc.document_Photo?.split("\\").pop() || "",
        boundaryName: doc.document_Boundaries?.split("\\").pop() || "",
        photoName: doc.document_Photo?.split("\\").pop() || "",
        poaDetailsName: doc.document_Fingerprint?.split("\\").pop() || "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Load details
      .addCase(loadDocumentDetails.pending, (state) => {
        state.status = "loadingDetails";
        state.error = null;
      })
      .addCase(loadDocumentDetails.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.details = payload;
      })
      .addCase(loadDocumentDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Preview deed
      .addCase(previewDeed.pending, (state) => {
        state.status = "loadingPreview";
        state.error = null;
      })
      .addCase(previewDeed.fulfilled, (state, { payload }) => {
        state.status = "previewReady";
        state.base64 = payload;
      })
      .addCase(previewDeed.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Submit docs
      .addCase(submitDocuments.pending, (state) => {
        state.status = "submitting";
        state.error = null;
      })
      .addCase(submitDocuments.fulfilled, (state) => {
        state.status = "submitted";
      })
      .addCase(submitDocuments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default uploadDocumentSlice.reducer;

// Selectors
export const selectUploadDetails = (state) => state.uploadDoc.details;
export const selectUploadStatus = (state) => state.uploadDoc.status;
export const selectUploadError = (state) => state.uploadDoc.error;
export const selectDeedBase64 = (state) => state.uploadDoc.base64;
export const { resetUploadDoc, setUploadDetails } = uploadDocumentSlice.actions;
